import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Loader2, Inbox, EyeOff } from "lucide-react";
import { api } from "../../api";
import { useApp } from "../../store";

export const StatsPanel = () => {
  const { t, lang } = useApp();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) {
    return (
      <div className="flex justify-center py-20" data-testid="admin-stats-loading">
        <Loader2 className="animate-spin text-[var(--accent)]" size={26} />
      </div>
    );
  }

  const locale = lang === "fr" ? "fr-FR" : "en-US";
  const data = stats.by_month.map((m) => {
    const [y, mo] = m.month.split("-").map(Number);
    return {
      name: new Date(y, mo - 1).toLocaleDateString(locale, { month: "short", year: "2-digit" }),
      count: m.count,
    };
  });

  return (
    <div data-testid="admin-stats-panel" className="flex flex-col gap-8">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="border border-white/10 bg-white/[0.02] p-6" data-testid="stats-total-card">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 grid place-items-center border border-[var(--accent)]/40 text-[var(--accent)]">
              <Inbox size={18} />
            </span>
            <span className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-white/50">{t("stats_total")}</span>
          </div>
          <p className="mt-4 font-display font-semibold text-4xl">{stats.total}</p>
        </div>
        <div className="border border-white/10 bg-white/[0.02] p-6" data-testid="stats-unread-card">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 grid place-items-center border border-[var(--accent)]/40 text-[var(--accent)]">
              <EyeOff size={18} />
            </span>
            <span className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-white/50">{t("stats_unread")}</span>
          </div>
          <p className="mt-4 font-display font-semibold text-4xl">{stats.unread}</p>
        </div>
      </div>

      <div className="border border-white/10 bg-white/[0.02] p-6" data-testid="stats-chart-card">
        <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-white/50 mb-6">{t("stats_chart_title")}</p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 0, right: 0, left: -22, bottom: 0 }}>
              <XAxis dataKey="name" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip
                cursor={{ fill: "rgba(249, 87, 56, 0.08)" }}
                contentStyle={{ background: "#172A45", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 0, fontSize: 12 }}
                labelStyle={{ color: "#fff" }}
                itemStyle={{ color: "var(--accent)" }}
              />
              <Bar dataKey="count" maxBarSize={48}>
                {data.map((_, i) => (
                  <Cell key={i} fill={i === data.length - 1 ? "var(--accent)" : "#1C2541"} stroke="var(--accent)" strokeOpacity={0.35} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
